import { formatPhoneDisplay } from './phone';

/**
 * Docgen-facing phone formatter. Delegates display formatting to the canonical
 * {@link formatPhoneDisplay} (issue #401: `555-123-4567`, extensions and
 * international numbers preserved) but keeps the historical `'N/A'` for an empty
 * value, because document templates and the token builder expect that sentinel.
 */
export function formatPhone(phone: string | null | undefined): string {
  return formatPhoneDisplay(phone) || 'N/A';
}

export function toTitleCase(str: string | null | undefined): string {
  if (!str) return 'N/A';
  return str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });
}

const STATE_MAPPINGS: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

export function getStateFullName(state: string | null | undefined): string {
  if (!state) return 'N/A';
  const upper = state.toUpperCase().trim();
  return STATE_MAPPINGS[upper] || state;
}

/** The two-letter code for a state given as a code ("tx") or a full name ("Texas"); null when it is neither. */
export function getStateCode(state: string | null | undefined): string | null {
  if (!state) return null;
  const trimmed = state.trim();
  const upper = trimmed.toUpperCase();
  if (STATE_MAPPINGS[upper]) return upper;
  const byName = Object.entries(STATE_MAPPINGS).find(([, name]) => name.toUpperCase() === upper);
  return byName ? byName[0] : null;
}

const STATUS_MAPPINGS: Record<string, string> = {
  'need_photos_field': 'Need Photos - Field',
  'need_photos_desk': 'Need Photos - Desk',
  'pending_review': 'Pending Review',
  'approved': 'Approved',
  'rejected': 'Rejected',
  // standard fallback fallback covers the rest
};

export function formatStatusLabel(status: string | null | undefined): string {
  if (!status) return 'N/A';
  if (STATUS_MAPPINGS[status]) {
    return STATUS_MAPPINGS[status];
  }
  return toTitleCase(status);
}
